//Example at : https://codesandbox.io/s/ws5dtr?file=/App.tsx:2254-2763
import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  CoffeeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  ActionType,
  ProTable,
  PageContainer,
} from "@ant-design/pro-components";
import {
  Space,
  Button,
  Drawer,
  theme,
  Col,
  Divider,
  Row,
  Typography,
  Timeline,
  Switch,
} from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CompanyContext } from "../../contexts/company-context";
import {
  ScheduleDetails,
  ScheduleReportQuery,
} from "../../serviceoperation/entity/ScheduleOperationEntity";
import { ScheduleOperation } from "../../serviceoperation/ScheduleOperation";
import {
  AmPMFormat,
  ConvertTo12hr,
  getFirstAndLastDayOfWeek,
} from "../../Util/dateutil";
import { EmployeeDirectReports } from "../../repository/EmployeeRepositoryEntity";
import { EmployeeOperation } from "../../serviceoperation/EmployeeOperation";
import dayjs from "dayjs";
import ScheduleActivityModify from "../schedule/schedule-activity-modify";
import { BranchOperation } from "../../serviceoperation/BranchOperation";
import renderSyncStatusAlert from "../schedule/helpers/reSyncStatusAlert";
import AddScheduleForm from "../schedule/modal/addSchedule";
import { ScheduleHistory, ScheduleHistoryRow } from "./types";
import { useReportData } from "./hooks/useReportData";
import { useHistoryWithKeys, useGroupedHistory } from "./utils/groupingUtils";
import { createReportColumns } from "./columns/reportColumns";

const Report = (): JSX.Element => {
  const { Paragraph } = Typography;

  const { selectedCompany } = useContext(CompanyContext);
  const employeeOps = new EmployeeOperation();
  const [deFaultPageSize] = useState<number>(14);
  const actionRef = useRef<ActionType>();
  const [empList, setEmployeeList] = useState<EmployeeDirectReports[]>([]);
  const [location, setLocation] = useState<{ value: number; label: string }[]>(
    [],
  );
  const [isManager, setIsManager] = useState<boolean>(false);
  const [groupByEmployee, setGroupByEmployee] = useState<boolean>(false);

  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [openAddSchedule, setOpenAddSchedule] = useState(false);

  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleHistory>();
  const [scheduleDetail, setScheduleDetail] = useState<ScheduleDetails>();
  const [reFetchSchedule, setReFetchSchedule] = useState<boolean>(false);

  // Use custom hook for data fetching
  const {
    history,
    setHistory,
    queryOptions,
    setQueryOptions,
    totalResult,
    isLoading,
    fetchScheduleReportData,
  } = useReportData(deFaultPageSize, reFetchSchedule);

  const { useToken } = theme;
  const { token } = useToken();
  const [open, setOpen] = useState<boolean>(false);

  interface DescriptionItemProps {
    title: string;
    content: React.ReactNode;
  }

  const DescriptionItem = ({ title, content }: DescriptionItemProps) => (
    <div style={{ color: token.colorTextDescription }}>
      <Paragraph
        style={{ color: token.colorTextBase }}
        className="site-description-item-profile-p-label"
      >
        {title}:
      </Paragraph>
      {content}
    </div>
  );

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (selectedSchedule) {
      const scheduleOps = new ScheduleOperation();
      scheduleOps
        .getScheduleByIdAsync(
          selectedCompany?.corporationId as number,
          selectedSchedule.scheduleId as number,
        )
        .then((x) => {
          setScheduleDetail(x);
        });
    }
  }, [selectedSchedule, selectedCompany]);

  useEffect(() => {
    const getEmployeeList = async () => {
      if (!selectedCompany) return;
      const employees = await employeeOps.getEmployeeAsync(
        selectedCompany.corporationId,
        1,
        255,
      );
      setEmployeeList(
        employees.results
          .filter((x) => x.isActive)
          .map((employee) => ({
            label: `${employee.firstName} ${employee.lastName}`,
            value: employee.employeeId,
          })),
      );
      setIsManager(employees.rowCount > 1);

      actionRef.current?.reload();
    };
    getEmployeeList();
  }, [selectedCompany]);

  const branchOps = useMemo(() => new BranchOperation(), []);

  useMemo(() => {
    branchOps
      .getBranchAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        if (value.data) {
          setLocation(
            value.data.map((s) => ({
              value: s.branchID,
              label: s.name,
            })),
          );
        }
      });
  }, [selectedCompany]);

  const date = new Date(); // use any date here
  const [firstDayOfWeek, lastDayOfWeek] = getFirstAndLastDayOfWeek(date);

  // Transform empList to EmployeeType format for AddScheduleForm
  const employeeForModal = useMemo(
    () =>
      empList.map((emp) => ({
        key: emp.value,
        name: emp.label,
      })),
    [empList],
  );

  // Use column factory function
  const col = useMemo(
    () =>
      createReportColumns(
        empList,
        location,
        firstDayOfWeek,
        lastDayOfWeek,
        groupByEmployee,
      ),
    [empList, location, firstDayOfWeek, lastDayOfWeek, groupByEmployee],
  );

  // Use custom hooks for data transformation
  const historyWithKeys = useHistoryWithKeys(history);
  const groupedHistory = useGroupedHistory(historyWithKeys);

  const detailColumns = useMemo(() => {
    const groupColumnTitles = [
      "Total Hours",
      "Days Scheduled",
      "Missing Activity",
      "Absent",
      "Range",
      "Employee",
      "Location",
    ];

    return col
      .filter((column) => !groupColumnTitles.includes(column.title as string))
      .map((column) => ({
        ...column,
        hideInTable: false,
      }));
  }, [col]);

  const handleQuerySubmit = (value: any) => {
    const startDate =
      value.startTime != "[object Object]"
        ? value.startTime
        : firstDayOfWeek.toDateString();
    const endDate =
      value.endTime != "[object Object]"
        ? value.endTime
        : lastDayOfWeek.toDateString();
    const employeeId = value.selectedEmpId;
    const branchId = value.selectedBranchId;
    const options: ScheduleReportQuery = {
      startDate,
      endDate,
      page: 1,
      pageSize: deFaultPageSize,
      IncludeDirReport: true,
    };

    if (employeeId && employeeId.trim() !== "") {
      options.employeeId = employeeId;
    }
    if (branchId) {
      options.branchId = branchId;
    }
    setQueryOptions(options);
    fetchScheduleReportData(options).then(setHistory);
  };

  const onPageChange = (page: number) => {
    queryOptions!.page = page;
    fetchScheduleReportData(queryOptions!).then(setHistory);
  };

  useEffect(() => {
    actionRef.current!.reset!();
    actionRef.current?.reload();
  }, [empList]);

  return (
    <PageContainer subTitle="Effortlessly generate insightful reports for past occurrences of your schedule.">
      <style>
        {`
          .missing-clockout-row td {
            background-color: ${token.colorErrorBg};
          }
          .absent-row td {
            background-color: ${token.colorWarningBg};
          }
        `}
      </style>
      <ProTable<ScheduleHistoryRow>
        loading={isLoading}
        columns={col}
        actionRef={actionRef}
        cardBordered
        dataSource={groupByEmployee ? groupedHistory : historyWithKeys}
        editable={{
          type: "multiple",
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              if (record.isGroup || record.isAbsent) return;
              setSelectedSchedule(record);
              setOpen(true);
              // You can navigate, show a modal, or perform any action here
            },
            style: {
              cursor:
                record.isGroup || record.isAbsent ? "not-allowed" : "pointer",
            },
            title: record.isAbsent
              ? "Absent schedules cannot have activity."
              : undefined,
          };
        }}
        expandable={
          groupByEmployee
            ? {
                rowExpandable: (record) => !!record.subRows?.length,
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.subRows?.length ? (
                    <Button
                      type="text"
                      size="small"
                      icon={
                        expanded ? <CloseCircleOutlined /> : <PlusOutlined />
                      }
                      onClick={(e) => onExpand(record, e)}
                    />
                  ) : null,
                expandedRowRender: (record) => (
                  <ProTable<ScheduleHistoryRow>
                    columns={detailColumns}
                    dataSource={record.subRows ?? []}
                    pagination={false}
                    search={false}
                    options={false}
                    rowKey={(row) => row.key}
                    size="small"
                    onRow={(row) => ({
                      onClick: () => {
                        if (row.isAbsent) return;
                        setSelectedSchedule(row);
                        setOpen(true);
                      },
                      style: {
                        cursor: row.isAbsent ? "not-allowed" : "pointer",
                      },
                      title: row.isAbsent
                        ? "Absent schedules cannot have activity."
                        : undefined,
                    })}
                    rowClassName={(row) =>
                      row.isAbsent
                        ? "absent-row"
                        : !row.endTime || row.endTime === "--"
                        ? "missing-clockout-row"
                        : ""
                    }
                  />
                ),
              }
            : undefined
        }
        rowClassName={(record) => (record.isAbsent ? "absent-row" : "")}
        summary={() => (
          <>
            {history ? (
              <ProTable.Summary fixed={"bottom"}>
                <ProTable.Summary.Row>
                  <ProTable.Summary.Cell index={0} colSpan={4}>
                    Total Hours
                  </ProTable.Summary.Cell>
                  <ProTable.Summary.Cell index={1} colSpan={4}>
                    {history
                      .filter((item) => item.employeeActivity?.totalHoursWorked)
                      .reduce(
                        (sum, current) =>
                          sum + current.employeeActivity!.totalHoursWorked!,
                        0,
                      )
                      .toFixed(2)}{" "}
                    Hours
                  </ProTable.Summary.Cell>
                </ProTable.Summary.Row>
              </ProTable.Summary>
            ) : null}
          </>
        )}
        columnsState={{
          persistenceKey: "pro-table-singe-demos",
          persistenceType: "localStorage",
          onChange(value) {
            //console.log("value: ", value);
          },
        }}
        rowKey={(record) => record.key}
        search={{
          labelWidth: "auto",
        }}
        options={{
          setting: {
            listsHeight: 400,
          },
        }}
        // On Query Submit Button
        onSubmit={(value) => {
          handleQuerySubmit(value);
        }}
        pagination={{
          pageSize: deFaultPageSize,
          total: totalResult,
          onChange: (page) => onPageChange(page),
        }}
        dateFormatter="string"
        headerTitle="Schedule History"
        toolBarRender={() => [
          <Space key="group-toggle">
            <Switch
              checked={groupByEmployee}
              onChange={(checked) => setGroupByEmployee(checked)}
            />
            <Typography.Text>Group by employee</Typography.Text>
          </Space>,
          isManager ? (
            <Button
              key="button"
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => setOpenAddSchedule(true)}
            >
              Add Schedule
            </Button>
          ) : null,
        ]}
      />
      <Drawer
        style={{ backgroundColor: token.colorBgContainer }}
        width={640}
        placement="right"
        closable={true}
        title={`${selectedSchedule?.employee.firstName} - Schedule ${dayjs(
          selectedSchedule?.scheduleDate,
        ).format("dddd, MMMM Do")}`}
        onClose={onClose}
        open={open}
      >
        <Row>
          <Col span={12}>
            <DescriptionItem
              title="Full Name"
              content={`${selectedSchedule?.employee?.firstName} ${selectedSchedule?.employee?.lastName}`}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title="Title"
              content={selectedSchedule?.employee?.title}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <DescriptionItem
              title="Schedule Id"
              content={selectedSchedule?.scheduleId}
            />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title="Schedule Date"
              content={dayjs(selectedSchedule?.scheduleDate).format(
                "dddd, MMMM Do",
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <DescriptionItem
              title="Schedule Time"
              content={
                <>
                  {ConvertTo12hr(scheduleDetail?.startWorkHour ?? 0)}:
                  {scheduleDetail?.startWorkMinute === 0
                    ? "00"
                    : scheduleDetail?.startWorkMinute}{" "}
                  {AmPMFormat(scheduleDetail?.startWorkHour ?? 0)}
                  {" - "}
                  {ConvertTo12hr(scheduleDetail?.endWorkHour ?? 0)}:
                  {scheduleDetail?.endWorkMinute === 0
                    ? "00"
                    : scheduleDetail?.endWorkMinute}{" "}
                  {AmPMFormat(scheduleDetail?.endWorkHour ?? 0)}
                </>
              }
            />
          </Col>
        </Row>
        <Divider />

        {renderSyncStatusAlert(
          scheduleDetail?.syncStatus,
          selectedSchedule?.scheduleId as number,
        )}

        <Paragraph>Activity</Paragraph>
        <Row>
          <Timeline mode="left" style={{ padding: "24px", paddingTop: "0" }}>
            {scheduleDetail?.shiftActivity.map((item) => (
              <Timeline.Item
                key={item.scheduleActivityID}
                dot={(() => {
                  switch (item.activityType) {
                    case "CLOCK_IN":
                      return (
                        <ClockCircleOutlined style={{ fontSize: "16px" }} />
                      );
                    case "BREAK_START":
                      return <CoffeeOutlined style={{ fontSize: "16px" }} />;
                    case "BREAK_END":
                      return <CoffeeOutlined style={{ fontSize: "16px" }} />;
                    case "CLOCK_OUT":
                      return (
                        <CloseCircleOutlined style={{ fontSize: "16px" }} />
                      );
                  }
                })()}
              >
                {(() => {
                  switch (item.activityType) {
                    case "CLOCK_IN":
                      return `Started Shift at: ${new Date(
                        item.activityDateTime + "Z",
                      ).toLocaleString()}`;
                    case "CLOCK_OUT":
                      return `Clocked out of shift at: ${new Date(
                        item.activityDateTime + "Z",
                      ).toLocaleString()}`;
                    case "BREAK_START":
                      return `Started Break at: ${new Date(
                        item.activityDateTime + "Z",
                      ).toLocaleString()}`;
                    case "BREAK_END":
                      return `Ended Break at at: ${new Date(
                        item.activityDateTime + "Z",
                      ).toLocaleString()}`;
                    default:
                      return null;
                  }
                })()}
              </Timeline.Item>
            ))}
          </Timeline>
        </Row>
        <Row>
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button type="primary" onClick={() => setOpenActivityModal(true)}>
              Edit Activity
            </Button>
          </Col>
        </Row>
      </Drawer>

      {openActivityModal && (
        <ScheduleActivityModify
          scheduleDetail={scheduleDetail!}
          open={true}
          onCreate={(data: any) => {
            setOpenActivityModal(false);
            setReFetchSchedule(!reFetchSchedule);
            // Set the Original Schedule Object so that it reflects the clock out date and time....
            // ToDO:
          }}
          onCancel={() => {
            setOpenActivityModal(false);
          }}
        />
      )}

      <AddScheduleForm
        open={openAddSchedule}
        employee={employeeForModal}
        onCreate={() => {
          setOpenAddSchedule(false);
          setReFetchSchedule(!reFetchSchedule);
        }}
        onCancel={() => {
          setOpenAddSchedule(false);
        }}
      />
    </PageContainer>
  );
};

export default Report;
